import type { ComponentTemplate } from "@v1/types/component-template";
import type {
  ComponentTemplateCategoryId,
  ComponentTemplateIndustryId,
} from "schemas/componentTemplates";
import type { ComponentTemplateScope } from "schemas/generated/componentTemplates";
import type { TemplateDevices } from "./ChooseTemplateOverlay";

import React, { useEffect, useRef, useState } from "react";

import { DebouncedInput } from "@v1/components/common/designSystem/Input";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { useIntervalOptions } from "@v1/utils/use-interval-options";
import { ArrowUp, Search } from "lucide-react";

import PreviewPlatformToggleGroup from "./PreviewPlatformToggleGroup";
import TemplatesGrid from "./TemplatesGrid";

const templatePrompts = [
  "Describe what you need. We'll find the template...",
  "Landing page with a comparison table...",
  "Product page with image gallery...",
  "Listicle page for dogs and cats...",
  "Black Friday page with discounts...",
  "About us page with values section...",
];

interface SearchTemplatesProps {
  selectedIndustryIds: ComponentTemplateIndustryId[];
  selectedCategoryIds: ComponentTemplateCategoryId[];
  setSubpageTemplate: (template: ComponentTemplate | null) => void;
  previewPlatform: TemplateDevices;
  setPreviewPlatform: (platform: TemplateDevices) => void;
  templateScope: Omit<ComponentTemplateScope, "left-bar" | "unlisted">;
}

const SearchTemplates: React.FC<SearchTemplatesProps> = ({
  selectedIndustryIds,
  selectedCategoryIds,
  setSubpageTemplate,
  previewPlatform,
  setPreviewPlatform,
  templateScope,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const placeholder = useIntervalOptions({
    options: templatePrompts,
    interval: 5000,
  });
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!gridRef.current) {
        return;
      }

      setShowScrollButton(gridRef.current.scrollTop > 300);
    };

    const currentRef = gridRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const scrollToTop = () => {
    if (gridRef.current) {
      gridRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative flex w-full flex-col gap-6">
      <div className="flex gap-3">
        <div className="w-full">
          <DebouncedInput
            placeholder={placeholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            startEnhancer={<Search className="h-4 w-4" />}
            autoComplete="off"
            size="base"
          />
        </div>
        <PreviewPlatformToggleGroup
          previewPlatform={previewPlatform}
          setPreviewPlatform={setPreviewPlatform}
        />
      </div>

      <TemplatesGrid
        searchTerm={searchTerm}
        selectedIndustryIds={selectedIndustryIds}
        selectedCategoryIds={selectedCategoryIds}
        setSubpageTemplate={setSubpageTemplate}
        previewPlatform={previewPlatform}
        gridRef={gridRef}
        templateScope={templateScope}
      />

      {showScrollButton && (
        <DeprecatedIconButton
          variant="secondary"
          onClick={scrollToTop}
          layoutClassName="fixed bottom-10 right-10"
          aria-label="Scroll to top"
          size="lg"
          icon={<ArrowUp size={20} />}
        />
      )}
    </div>
  );
};

export default SearchTemplates;
